import { use, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { getEnv } from '../../lib/env';
import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps, isModuleEnabled } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import Tooltip from '../../components/Tooltip/Tooltip';

import ContentObjectFormContext from '../../contexts/ContentObjectFormContext';
import UserContext from '../../contexts/UserContext';

import { useAppForm } from '../../form-hooks/form';

const DEFAULT_CODE_EDITOR =
  'function _webhook(ctx) {\n' +
  '        const { payload } = ctx;\n' +
  '        const newPayload = { ...payload };\n\n' +
  '        // Example: Payload validation\n' +
  "        // if ( payload['category'] === 'book' && !payload['isbn'])\n" +
  "        // return { errors: {'isbn': ['Error: ISBN is required for books.'] } };\n\n" +
  '        // Example: Payload modifications\n' +
  "        // newPayload['slug'] = payload['title']\n" +
  '        //     .toLowerCase()\n' +
  "        //     .replace(/[^a-z0-9]+/g, '-')\n" +
  "        //     .replace(/(^-|-$)/g, '');\n\n" +
  '        return { payload: newPayload };\n' +
  '}';

const getInitialValues = (webhook) => ({
  name: webhook?.name || '',
  url: webhook?.url || '',
  contentTypeDefinitions:
    webhook?.content_type_definitions?.map(
      (el) => el.content_type_definition_name,
    ) || [],
  actions: webhook?.actions || [],
  headers: webhook?.headers || [],
  enabled: webhook?.enabled || false,
  type: webhook?.type || 'async',
  executionMode: webhook?.executionMode || 'url',
  code: webhook?.code || DEFAULT_CODE_EDITOR,
});

const WebhookForm = ({
  webhook,
  webhookContentType,
  onSubmit = EMPTY_FUNCTION,
  mode,
  userPlugins,
  disabled,
  codeWebhookAvailable,
  contentTypesOptions = EMPTY_ARRAY,
  filterCtdName = EMPTY_FUNCTION,
  setFormState,
  testId,
}) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);

  const typeOptions = useMemo(() => {
    return [
      {
        value: 'async',
        label: t('Webhooks.TypeOptions.Asynchronous'),
      },
      isModuleEnabled('WEBHOOKS_TYPE') && {
        value: 'sync',
        label: t('Webhooks.TypeOptions.Synchronous'),
      },
      {
        value: 'ctd',
        label: t('Webhooks.TypeOptions.Ctd'),
      },
    ].filter(Boolean);
  }, [t]);

  const isFieldVisible = useCallback(
    (fieldName) =>
      permissions.canCo(
        '_webhooks',
        RolePermissions.PERMISSIONS_TYPES.READ,
        fieldName,
      ),
    [permissions],
  );

  const isFieldDisabled = useCallback(
    (fieldName) =>
      !permissions.canCo(
        '_webhooks',
        mode === 'edit'
          ? RolePermissions.PERMISSIONS_TYPES.UPDATE
          : RolePermissions.PERMISSIONS_TYPES.CREATE,
        fieldName,
      ),
    [mode, permissions],
  );

  const contentObjectFormContextValue = useMemo(
    () => ({
      contentType: webhookContentType,
      initialData: webhook,
      userPlugins,
    }),
    [userPlugins, webhook, webhookContentType],
  );

  const executionModeOptions = useMemo(
    () => [
      {
        value: 'url',
        label: 'URL',
      },
      {
        value: 'code',
        label: codeWebhookAvailable ? (
          <>Code</>
        ) : (
          <Tooltip
            tooltip={t('Webhooks.Code.LimitsHostedWebhook')}
            {...getTestProps(testId, 'execution-mode-code', 'testId')}
          >
            Code
          </Tooltip>
        ),
      },
    ],
    [codeWebhookAvailable, t, testId],
  );

  const executionModeListeners = useMemo(
    () => ({
      onChange: ({ value, fieldApi }) => {
        if (value === 'code' && !fieldApi.form.getFieldValue('code')) {
          fieldApi.form.setFieldValue('code', DEFAULT_CODE_EDITOR);
        }
      },
    }),
    [],
  );

  const typeListeners = useMemo(
    () => ({
      onChange: ({ value, fieldApi }) => {
        if (value !== 'sync') {
          fieldApi.form.setFieldValue('executionMode', 'url');
        }
      },
    }),
    [],
  );

  const monacoEditorCaption = useMemo(() => {
    if (codeWebhookAvailable) {
      return (
        <Trans i18nKey="Webhooks.CodeEditorCaption">
          Use code editor to handle sync webhook. Read more, examples in
          <a
            className="underline text-sm text-slate-400"
            href={getEnv('REACT_APP_DOCUMENTATION_WEBHOOKS_CODE')}
            target="_blank"
            rel="noreferrer"
            {...getTestProps(testId, 'code-editor-docs-link')}
          >
            docs
          </a>
        </Trans>
      );
    }
    return (
      <Trans i18nKey="Webhooks.Code.DisabledCodeEditorCaption">
        Hosted webhooks limit exceeded, click this
        <a
          className="underline text-sm text-slate-400"
          href={getEnv('REACT_APP_PRICING')}
          target="_blank"
          rel="noreferrer"
          {...getTestProps(testId, 'code-editor-pricing-link')}
        >
          link
        </a>
        to upgrade
      </Trans>
    );
  }, [codeWebhookAvailable, testId]);

  const validationSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().min(1, t('Form.FormErrorMinimumOneCharacter')),
          url: z.string().optional(),
          code: z.string().optional(),
          type: z
            .string()
            .min(1, t('Form.FormErrorNotBlank'))
            .refine((value) => typeOptions.find((el) => el.value === value), {
              message: t('Webhooks.Errors.SelectedNoLongerValid'),
            }),
          executionMode: z.string().optional(),
          contentTypeDefinitions: z.array(z.string()).optional(),
          actions: z.array(z.object({ action: z.string() })).optional(),
          headers: z.array(
            z.object({
              header_name: z
                .string()
                .min(1, t('Form.FormErrorMinimumOneCharacter')),
              header_value: z
                .string()
                .min(1, t('Form.FormErrorMinimumOneCharacter')),
            }),
          ),
        })
        .superRefine((webhook, context) => {
          if (webhook.executionMode === 'code' && !webhook.code) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['code'],
              message: t('Form.FormErrorMinimumOneCharacter'),
            });
          }

          if (webhook.executionMode !== 'code' && !webhook.url) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['url'],
              message: t('Form.FormErrorMinimumOneCharacter'),
            });
          }

          if (webhook.type !== 'ctd' && webhook.actions.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['actions'],
              message: t('PropertyForm.Errors.OptionsLength'),
            });
          }
        }),
    [t, typeOptions],
  );

  const onFormSubmit = useCallback(
    async ({ value, formApi }) => {
      const [newWebhook, errors] = await onSubmit(value);
      if (!errors) {
        formApi.reset(getInitialValues(newWebhook));
      } else {
        setFormSubmitErrors(formApi, errors);
      }
    },
    [onSubmit],
  );

  const form = useAppForm({
    defaultValues: getInitialValues(webhook),
    onSubmit: onFormSubmit,
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <ContentObjectFormContext value={contentObjectFormContextValue}>
      <form.AppForm>
        <form
          id="webhook-form"
          className="flex flex-col w-full max-w-3xl px-5 m-auto pt-10"
          onSubmit={handleSubmit}
          noValidate={true}
        >
          <form.CustomFormElement />

          {isFieldVisible('name') && (
            <form.AppField name="name">
              {(field) => (
                <field.Input
                  label={t('Global.Name')}
                  additionalClasses="mb-4"
                  disabled={isFieldDisabled('name') || disabled}
                  required
                  {...getTestProps(testId, 'name', 'testId')}
                />
              )}
            </form.AppField>
          )}

          {isFieldVisible('type') && (
            <form.AppField name="type" listeners={typeListeners}>
              {(field) => (
                <field.Dropdown
                  label={t('Webhooks.Type')}
                  options={typeOptions}
                  debounceTime={150}
                  placeholder={t('Webhooks.Type')}
                  additionalClasses={'mb-4'}
                  disabled={isFieldDisabled('type') || disabled}
                  required
                  {...getTestProps(testId, `dropdown-type`, 'testId')}
                />
              )}
            </form.AppField>
          )}

          {isFieldVisible('executionMode') && (
            <form.Subscribe selector={({ values }) => values.type}>
              {(type) =>
                type === 'sync' && (
                  <form.AppField
                    name="executionMode"
                    listeners={executionModeListeners}
                  >
                    {(field) => (
                      <field.RadioGroup
                        options={executionModeOptions}
                        horizontal={true}
                        additionalClasses={'mb-4'}
                        disabled={isFieldDisabled('executionMode') || disabled}
                        required
                        {...getTestProps(testId, 'execution-mode', 'testId')}
                      />
                    )}
                  </form.AppField>
                )
              }
            </form.Subscribe>
          )}

          {isFieldVisible('url') && (
            <form.Subscribe
              selector={({ values }) => ({
                executionMode: values.executionMode,
                type: values.type,
              })}
            >
              {({ executionMode, type }) =>
                executionMode !== 'code' && (
                  <form.AppField name="url">
                    {(field) => (
                      <field.Input
                        label={type !== 'sync' ? 'URL' : ''}
                        additionalClasses={'mb-4'}
                        disabled={isFieldDisabled('url') || disabled}
                        required
                        {...getTestProps(testId, 'url', 'testId')}
                      />
                    )}
                  </form.AppField>
                )
              }
            </form.Subscribe>
          )}

          {isFieldVisible('code') && (
            <form.Subscribe selector={({ values }) => values.executionMode}>
              {(executionMode) =>
                executionMode === 'code' && (
                  <form.AppField name="code">
                    {(field) => (
                      <field.CodeEditor
                        options={{
                          readOnly:
                            isFieldDisabled('code') ||
                            !codeWebhookAvailable ||
                            disabled,
                          readOnlyMessage: {
                            value: disabled
                              ? ''
                              : t('Webhooks.Code.LimitsHostedWebhook'),
                          },
                        }}
                        className={twMerge(
                          'mb-4',
                          !codeWebhookAvailable ? 'opacity-65' : '',
                        )}
                        caption={monacoEditorCaption}
                        {...getTestProps(testId, 'code-editor', 'testId')}
                      />
                    )}
                  </form.AppField>
                )
              }
            </form.Subscribe>
          )}

          {isFieldVisible('enabled') && (
            <form.AppField name="enabled">
              {(field) => (
                <field.Switch
                  label={t('Webhooks.Enabled')}
                  additionalClasses={'mb-4'}
                  disabled={isFieldDisabled('enabled') || disabled}
                  {...getTestProps(testId, 'switch-enabled', 'testId')}
                />
              )}
            </form.AppField>
          )}

          {isFieldVisible('actions') && (
            <form.Subscribe selector={({ values }) => values.type}>
              {(type) =>
                type !== 'ctd' && (
                  <form.AppField name="actions" mode="array">
                    {(field) => (
                      <field.ActionsField
                        disabled={isFieldDisabled('actions') || disabled}
                        {...getTestProps(testId, 'actions', 'testId')}
                      />
                    )}
                  </form.AppField>
                )
              }
            </form.Subscribe>
          )}

          {isFieldVisible('content_type_definitions') && (
            <form.Subscribe selector={({ values }) => values.type}>
              {(type) =>
                type !== 'ctd' && (
                  <>
                    <label className="block text-sm text-slate-400 mb-2">
                      {t('Webhooks.ContentTypeDefinitions')}
                    </label>

                    <form.AppField name="contentTypeDefinitions">
                      {(field) => (
                        <field.Dropdown
                          options={contentTypesOptions}
                          multiple
                          filterCallback={filterCtdName}
                          debounceTime={150}
                          nullable
                          placeholder={t('Webhooks.All')}
                          placeholderMultiple={t(
                            'Webhooks.ContentTypeDefinitions',
                          )}
                          disabled={
                            isFieldDisabled('content_type_definitions') ||
                            disabled
                          }
                          {...getTestProps(testId, `dropdown`, 'testId')}
                        />
                      )}
                    </form.AppField>
                  </>
                )
              }
            </form.Subscribe>
          )}

          {isFieldVisible('headers') && (
            <>
              <label className="flex text-sm text-slate-400 mt-4 mb-4">
                {t('Webhooks.Headers')}
              </label>

              <form.AppField name="headers" mode="array">
                {(field) => (
                  <field.HeadersField
                    disabled={isFieldDisabled('headers') || disabled}
                    {...getTestProps(testId, 'headers', 'testId')}
                  />
                )}
              </form.AppField>
            </>
          )}
        </form>

        <form.FormStateHandler setFormikState={setFormState} />
        <form.DirtyHandler />
        <form.ValidationToastHandler hideToast />
      </form.AppForm>
    </ContentObjectFormContext>
  );
};

export default WebhookForm;
